import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Trash2, Mail, Phone, MessageSquare, RefreshCw } from "lucide-react";

const TYPE_LABEL = { contact: "Contact", venue: "Venue Inquiry" };

const TRIPLESEAT_STATUS = {
  sent: { label: "Sent to Tripleseat", dot: "bg-green-500", text: "text-green-700", bg: "bg-green-50 border-green-200" },
  pending: { label: "Pending", dot: "bg-amber-500", text: "text-amber-700", bg: "bg-amber-50 border-amber-200" },
  failed: { label: "Failed — not in Tripleseat", dot: "bg-red-500", text: "text-red-700", bg: "bg-red-50 border-red-200" },
};

const tripleseatState = (ts) => {
  if (!ts || !ts.status) return "pending";
  if (ts.status === "sent") return "sent";
  if (ts.status === "skipped") return "pending";
  return "failed"; // error | timeout | http_error | exception | failed
};

const TripleseatBadge = ({ lead, onRetry, retrying }) => {
  if (lead.type !== "venue") return null;
  const key = tripleseatState(lead.tripleseat);
  const s = TRIPLESEAT_STATUS[key];
  const title = key === "failed" && lead.tripleseat?.error ? JSON.stringify(lead.tripleseat.error) : undefined;
  return (
    <span className="inline-flex items-center gap-1.5">
      <span
        data-testid={`lead-tripleseat-${lead.id}`}
        data-tripleseat-status={key}
        title={title}
        className={`inline-flex items-center gap-1.5 text-[11px] font-medium border px-2 py-0.5 rounded-full ${s.bg} ${s.text}`}
      >
        <span className={`w-1.5 h-1.5 rounded-full ${s.dot}`} />
        {s.label}
        {key === "sent" && lead.tripleseat?.lead_id ? ` #${lead.tripleseat.lead_id}` : ""}
      </span>
      {key === "failed" && (
        <button
          onClick={() => onRetry(lead.id)}
          disabled={retrying === lead.id}
          data-testid={`lead-tripleseat-retry-${lead.id}`}
          className="inline-flex items-center gap-1 text-[11px] font-medium text-red-700 hover:text-red-900 border border-red-200 hover:bg-red-50 px-2 py-0.5 rounded-full disabled:opacity-50"
        >
          {retrying === lead.id ? <Loader2 size={11} className="animate-spin" /> : <RefreshCw size={11} />}
          Retry
        </button>
      )}
    </span>
  );
};

const Leads = () => {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [retrying, setRetrying] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get("/api/leads");
        if (alive) setLeads(data);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  const onRetry = async (id) => {
    setRetrying(id);
    try {
      const { data } = await api.post(`/api/leads/${id}/tripleseat-retry`);
      setLeads((l) => l.map((x) => (x.id === id ? { ...x, tripleseat: data.tripleseat } : x)));
      if (data.tripleseat?.status === "sent") toast.success("Sent to Tripleseat");
      else toast.error("Still failing — lead is saved locally");
    } catch {
      toast.error("Retry failed");
    } finally {
      setRetrying(null);
    }
  };

  const onDelete = async (id) => {
    if (!window.confirm("Delete this message?")) return;
    await api.delete(`/api/leads/${id}`);
    setLeads((l) => l.filter((x) => x.id !== id));
    toast.success("Deleted");
  };

  if (loading)
    return <div className="flex items-center justify-center py-20"><Loader2 className="animate-spin text-zinc-400" /></div>;

  return (
    <div data-testid="leads-page">
      <h1 className="text-2xl font-medium text-zinc-900">Inquiries</h1>
      <p className="text-sm text-zinc-500 mt-1">Messages from the Contact and Venue inquiry forms.</p>

      {leads.length === 0 ? (
        <div className="mt-8 text-center py-20 text-sm text-zinc-500 border border-dashed border-zinc-300 rounded-lg">No messages yet.</div>
      ) : (
        <ul className="mt-8 space-y-4">
          {leads.map((l) => (
            <li key={l.id} data-testid={`lead-${l.id}`} className="border border-zinc-200 rounded-lg bg-white p-5">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-zinc-900">{l.firstName} {l.lastName}</span>
                    <span className="text-[10px] tracking-wider uppercase text-zinc-600 bg-zinc-100 border border-zinc-200 px-2 py-0.5 rounded-full">{TYPE_LABEL[l.type] || l.type}</span>
                    <TripleseatBadge lead={l} onRetry={onRetry} retrying={retrying} />
                  </div>
                  <div className="mt-2 flex flex-wrap gap-x-5 gap-y-1 text-xs text-zinc-600">
                    {l.email && <a href={`mailto:${l.email}`} className="inline-flex items-center gap-1 hover:text-zinc-900"><Mail size={12} /> {l.email}</a>}
                    {l.phone && <a href={`tel:${l.phone}`} className="inline-flex items-center gap-1 hover:text-zinc-900"><Phone size={12} /> {l.phone}</a>}
                  </div>
                </div>
                <button onClick={() => onDelete(l.id)} data-testid={`lead-delete-${l.id}`} className="text-red-500 hover:text-red-700 shrink-0"><Trash2 size={15} /></button>
              </div>
              {l.message && (
                <p className="mt-3 text-sm text-zinc-700 flex gap-2"><MessageSquare size={14} className="text-zinc-400 mt-0.5 shrink-0" /> {l.message}</p>
              )}
              {l.meta && Object.keys(l.meta).length > 0 && (
                <div className="mt-3 flex flex-wrap gap-2">
                  {Object.entries(l.meta).filter(([, v]) => v).map(([k, v]) => (
                    <span key={k} className="text-[11px] text-zinc-600 bg-zinc-50 border border-zinc-200 rounded px-2 py-0.5">{k}: {String(v)}</span>
                  ))}
                </div>
              )}
              <p className="mt-3 text-[11px] text-zinc-400">{new Date(l.created_at).toLocaleString()}</p>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default Leads;
